/**
 * Mutators: physics modifiers that multiply existing sectors into new
 * challenges. Each one patches controller parameters (player AND rival, so
 * the race stays fair) and restores them exactly. Every mutator carries a
 * curated sector pool: combinations that would fight a sector's tuned
 * geometry (low gravity vs speed-gate flight heights, heavy vs pad arcs)
 * simply never come up.
 */

export const MUTATORS = [
  {
    id: 'lowg',
    name: 'LOW-G',
    blurb: 'Gravity is a suggestion.',
    sectors: ['freefall', 'uplink', 'cascade', 'rebound', 'aurora', 'descent'],
    patch: { gravity: (g) => g * 0.55 },
  },
  {
    id: 'heavy',
    name: 'HEAVY',
    blurb: 'Down wants you badly.',
    sectors: ['freefall', 'uplink', 'spire', 'rebound', 'descent'],
    patch: { gravity: (g) => g * 1.5, jumpVelocity: (v) => v * 1.15 },
  },
  {
    id: 'ice',
    name: 'ICE',
    blurb: 'The floors forgot friction.',
    sectors: ['boot', 'freefall', 'uplink', 'spire', 'velocity', 'descent',
      'cascade', 'turbine', 'rebound', 'aurora', 'conflux'],
    patch: { friction: (f) => f * 0.12, groundAccel: (a) => a * 0.5 },
  },
  {
    id: 'lockin',
    name: 'LOCKED-IN',
    blurb: 'No mid-air corrections. Commit.',
    sectors: ['boot', 'freefall', 'uplink', 'rebound', 'aurora', 'cascade'],
    patch: { airWishCap: () => 0 },
  },
  {
    id: 'decay',
    name: 'DECAY',
    blurb: 'Portals rot in ten seconds. Keep shooting.',
    // Portal-heavy sectors, where re-shooting on the move IS the challenge.
    sectors: ['boot', 'freefall', 'uplink', 'velocity', 'cascade', 'conflux'],
    // Not a controller patch: portalDecay rides the same slot on the game's
    // portal system (Game.startLevel), player and rival pairs alike.
    patch: {},
    portalDecay: 10,
    // A new mutator changes the daily hash pool, and a mid-history change
    // would SPLIT a live day's board between old and new clients (the
    // g20260820 two-tower lesson). `since` keys the mutator to dates from
    // which every client computes the same pool; two days out also gives
    // installed PWAs time to pick up the bundle that knows this mutator.
    since: 20260823,
  },
];

/** Deterministic pick for a UTC date: everyone races the same combo. The
 *  pool admits a mutator only from its `since` date, so history (and any
 *  client computing an older date) never reshuffles. */
export function dailyCombo(date = new Date()) {
  const key = date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
  // xorshift-ish scramble; stable across engines, no Math.random.
  let h = key ^ 0x9e3779b9;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
  h = Math.imul(h ^ (h >>> 13), 0x45d9f3b);
  h = (h ^ (h >>> 16)) >>> 0;
  const pool = MUTATORS.filter((m) => !m.since || key >= m.since);
  const mutator = pool[h % pool.length];
  const sector = mutator.sectors[Math.floor(h / 7) % mutator.sectors.length];
  return { mutator, sector, board: `d${key}` };
}

/**
 * The knobs a level def's `physics` block may override, and the editor's
 * playground exposes: [key, min, max, step, tip]. The keys double as the
 * whitelist (everything else on a controller is off-limits by
 * construction); the ranges are generous tuning room, not guarantees; the
 * tip is the hover explanation shown in the drawer.
 */
export const PHYS_KNOBS = [
  ['gravity', -60, -5, 0.5,
    'Downward pull (m/s2). More negative = snappier, shorter arcs. Rescales every jump and drop in the level.'],
  ['jumpVelocity', 3, 20, 0.1,
    'Upward launch speed of a jump. Height is v*v / (2 * gravity): raising this is punchier than softening gravity.'],
  ['walkSpeed', 3, 16, 0.1,
    'Ground target speed. Sector drift laws are tuned to it, and hops carry whatever speed you leave the ground with.'],
  ['sprintSpeed', 4, 24, 0.1,
    'Sprint target speed. Mid-air the walk/sprint distinction stops mattering; this sets how hot you can enter a jump.'],
  ['groundAccel', 2, 40, 0.5,
    'How fast you reach target speed on the ground. Low = car-like spool-up; ICE halves it.'],
  ['friction', 0.5, 20, 0.1,
    'Ground speed bleed, the ONLY thing in the game that eats momentum. Jumping before it bites is why bunny-hopping works.'],
  ['stopSpeed', 0, 8, 0.1,
    'Below this speed friction clamps you to a dead stop instead of decaying forever.'],
  ['airAccel', 5, 120, 1,
    'Drift strength of the low-speed air regime (below the cap). High = near-instant drift authority.'],
  ['airWishCap', 0, 12, 0.1,
    'The heart of air control. Below this speed: full drift authority. Above: input only STEERS. Zero = locked (no air control).'],
  ['airTurnRate', 0, 3, 0.05,
    'Banking rate above the blend band, in rad/s, with speed CONSERVED. High = paper-plane agility at any speed.'],
  ['airBrake', 0, 40, 0.5,
    'Speed trim (m/s2) when pulling against your motion at high speed. Bounded; never drops you below the cap.'],
  ['airBlendLo', 0, 12, 0.1,
    'Speed where drift begins fading into banking. Narrow band = two distinct feels; wide = one continuous character.'],
  ['airBlendHi', 0.5, 20, 0.1,
    'Speed where banking has fully taken over. Held above LO automatically.'],
  ['airTurnEase', 0.02, 1, 0.01,
    'Seconds for a bank to reach full rate (and to decay after release). Low = twitchy; high = long graceful arcs.'],
  ['coyoteTime', 0, 0.5, 0.01,
    'Grace window to jump after walking off an edge. Zero makes edge jumps frame-perfect.'],
  ['jumpBuffer', 0, 0.5, 0.01,
    'A jump pressed this early still fires on landing; also what makes auto-hop chains seamless.'],
];
export const PHYSICS_KEYS = PHYS_KNOBS.map(([key]) => key);

/**
 * Apply a def's absolute physics overrides; returns a restore function.
 * Mutator discipline: patch player and rival both, restore exactly (in
 * reverse, since the band guard may re-touch a key already overridden).
 */
export function applyPhysics(overrides, controllers) {
  const undo = [];
  for (const c of controllers) {
    for (const key of PHYSICS_KEYS) {
      const v = overrides?.[key];
      if (!Number.isFinite(v)) continue;
      const prev = c[key];
      undo.push(() => { c[key] = prev; });
      c[key] = v;
    }
    // The air-steer blend weight divides by (hi - lo); a degenerate band
    // would NaN every airborne frame, so hi is held above lo regardless.
    if (c.airBlendHi <= c.airBlendLo) {
      const prev = c.airBlendHi;
      undo.push(() => { c.airBlendHi = prev; });
      c.airBlendHi = c.airBlendLo + 0.5;
    }
  }
  return () => { for (let i = undo.length - 1; i >= 0; i--) undo[i](); };
}

/** Apply a mutator to a set of controllers; returns a restore function. */
export function applyMutator(mutator, controllers) {
  const undo = [];
  for (const c of controllers) {
    for (const [key, fn] of Object.entries(mutator.patch)) {
      const prev = c[key];
      undo.push(() => { c[key] = prev; });
      c[key] = fn(prev);
    }
  }
  return () => { for (const u of undo) u(); };
}
